//
// TypedMetadataField
//
// Return a Field for a metadata field based on type

import { observer } from 'mobx-react-lite'
import { useEffect, useState } from 'react'
import { useElementWidth } from '../../../../hooks/useElementWidth'
import { useStore } from '../../../../stores'

import { HView } from '../../../../appview'
import { BooleanInput, Icon, Item, Text } from '../../../../components'
import { AssetThumb } from '../../../../panels'
import { LAYOUTCOMPONENTTYPECONFIG } from '../../../../stores/data/LAYOUTCOMPONENTTYPECONFIG'
import { formatItemTitle } from '../../../../stores/data/utils'
import { mergeTransforms, Transform } from '../../../../stores/imageservers/utils'
import { ItemTitle } from '../../../components'

const PreviewImageField = observer(function PreviewImageField({
    name,
    field,
    value,
    component,
    record,
    worksheet,
}) {
    const { data } = useStore()
    const [containerRef, containerWidth] = useElementWidth(20)

    const [asset, setAsset] = useState(null)
    useEffect(() => {
        let isMounted = true
        let controller = new AbortController()
        async function asyncEffect() {
            setAsset(null)
            if (value) {
                if (!data.assets.has(value)) {
                    await data.fetchAssetByGid(value, { signal: controller.signal })
                }
                const asyncasset = data.assets.get(value)
                if (isMounted) {
                    setAsset(asyncasset)
                }
            }
        }
        asyncEffect()
        return () => {
            controller.abort('Unmounted')
            isMounted = false
        }
    }, [value, data])

    const config = LAYOUTCOMPONENTTYPECONFIG['metadata']['typeconfig'][field.type]
    const componentformat = config.thumbformat
        ? component
            ? component.thumbformat || config.thumbformat.default
            : config.thumbformat.default
        : undefined

    // default: fit, square
    let transform_fit = 'fit'
    let transform_width = containerWidth
    let transform_height = transform_width

    if (componentformat) {
        if (componentformat.startsWith('fill_')) {
            transform_fit = 'fill'
        }
        if (componentformat.endsWith('_banner')) {
            transform_height = (6 / 20) * transform_width
        }
        if (componentformat.endsWith('_landscape')) {
            transform_height = (9 / 16) * transform_width
        }
        if (componentformat.endsWith('_portrait')) {
            transform_height = (16 / 9) * transform_width
        }
        if (componentformat.endsWith('_pillar')) {
            transform_height = (20 / 6) * transform_width
        }
        if (componentformat === 'proportional' && asset) {
            const asset_width = asset.info.get('width')
            const asset_height = asset.info.get('height')
            if (asset_width && asset_height) {
                transform_height = (asset_height / asset_width) * transform_width
            }
        }
    }

    const transformResize = new Transform(
        transform_fit,
        transform_width,
        transform_height
    )
    const transform = asset
        ? mergeTransforms(asset.mainTransform, transformResize)
        : transformResize

    let attributes = {
        className: 'ws-metadatapreview ws-preview-field ws-field-' + field.type,
    }
    if (component.style) attributes['className'] += ' lc-' + component.style

    return (
        <div {...attributes} ref={containerRef}>
            {!asset ? undefined : (
                <AssetThumb
                    asset={asset}
                    transform={transform}
                    iconplaceholder="image"
                />
            )}
        </div>
    )
})

const PreviewBooleanField = observer(function PreviewBooleanField({
    name,
    field,
    value,
    component,
    record,
    worksheet,
}) {
    const classes = ['ws-metadatapreview', 'ws-preview-field', 'ws-field-' + field.type]

    return (
        <div className={classes.join(' ')}>
            <HView vcenter gap={3}>
                <BooleanInput value={value} />
                {name}
            </HView>
        </div>
    )
})

const PreviewTextlistField = observer(function PreviewTextlistField({
    name,
    field,
    value,
    component,
    record,
    worksheet,
}) {
    const config = LAYOUTCOMPONENTTYPECONFIG['metadata']

    const classes = ['ws-metadatapreview', 'ws-preview-field', 'ws-field-' + field.type]
    const componentstyle = component.style
        ? component.style
        : config.typeconfig[field.type].style.default
    if (componentstyle) classes.push('lc-' + componentstyle)

    if (Array.isArray(value)) {
        if (componentstyle === 'bullets') {
            value = value.map((line, index) => <li key={index}>{line}</li>)
            value = <ul>{value}</ul>
        } else {
            value = value.join('\n')
        }
    }

    return <Text className={classes.join(' ')}>{value}</Text>
})

const PreviewFieldpickerField = observer(function PreviewFieldpickerField({
    name,
    field,
    value,
    component,
    record,
    worksheet,
}) {
    const { data, view } = useStore()

    const linkedfieldgids = value ? value.split(/\./) : []
    const linkedfields = linkedfieldgids.map(gid =>
        data.fields.has(gid) ? data.fields.get(gid) : undefined
    )

    const classes = ['ws-metadatapreview', 'ws-preview-field', 'ws-field-' + field.type]

    const subfieldtitles = linkedfields.map((subfield, index) => {
        const title = subfield
            ? formatItemTitle(
                  subfield,
                  view.language,
                  view.environment.get('labelView')
              )
            : undefined
        if (!title) {
            return null
        }
        return (
            <Item key={index} indent={2 * index}>
                <Icon name="field" size="text" />{' '}
                <ItemTitle item={subfield} ellipsisAfter={32} />
            </Item>
        )
    })

    return <div className={classes.join(' ')}>{subfieldtitles}</div>
})

export const TypedMetadataField = observer(function TypedMetadataField({
    name,
    field,
    component,
    record,
    worksheet,
}) {
    const { view } = useStore()
    const language = view.environment.get('language')

    if (!field) {
        return null
    }
    let value = field.is_localized ? field.value[language] : field.value

    if (Array.isArray(value) && field.type !== 'textlist') {
        value = value.join('\n')
    }

    let classes = ['ws-metadatapreview', 'ws-text', 'ws-field-' + field.type]

    const sharedAttributes = {
        name,
        field,
        value,
        record,
        component,
        worksheet,
    }

    switch (field.type) {
        case 'image':
            return <PreviewImageField {...sharedAttributes} />
        case 'fieldpicker':
            return <PreviewFieldpickerField {...sharedAttributes} />
        case 'boolean':
            return <PreviewBooleanField {...sharedAttributes} />
        case 'textlist':
            return <PreviewTextlistField {...sharedAttributes} />
        case 'text':
        case 'textline':
        case 'number':
        case 'decimal':
        default:
            if (component.style) {
                classes.push('lc-' + component.style)
            }
            if (component.gid === worksheet.layoutstore.selectedComponentGid) {
                classes.push('cc-selected')
            }
            return <Text className={classes.join(' ')}>{value}</Text>
    }
})
